import { useState } from "react";
import { format } from "date-fns";
import { Video, ListChecks, ExternalLink } from "lucide-react";
import { Card, CardContent } from "../../../../../components/ui/card";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import EmptyTabState from "../../../../../shared/ui/EmptyTabState";
import MarkdownRenderer from "../../../../../components/agno/MarkdownRenderer";
import ObjectDetailSheet from "./ObjectDetailSheet";

type Props = {
  records: any[];
  searchQuery?: string;
};

function safeFormat(value: any, fmt: string): string {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return format(d, fmt);
  } catch {
    return "";
  }
}

function parseList(value: any): string[] {
  if (Array.isArray(value)) return value.filter((v) => typeof v === "string");
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter((v: any) => typeof v === "string");
    } catch { /* ignore */ }
  }
  return [];
}

function getField(record: any, ...keys: string[]): string {
  for (const k of keys) {
    if (record[k]) return record[k];
  }
  return "";
}

function matchesSearch(record: any, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  const haystack = [
    record.summary_md, record.native_summary_md,
    record.transcript_text, record.native_transcript_text,
    record.provider, record.native_provider,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(q);
}

const RecordingCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  const summary = getField(record, "summary_md", "native_summary_md");
  const actionItems = parseList(record.action_items || record.native_action_items);
  const provider = getField(record, "provider", "native_provider", "source", "native_source");
  const language = getField(record, "language", "native_language");
  const recordingUrl = getField(record, "recording_url", "native_recording_url");
  const date = safeFormat(
    record.created_at_tz || record.native_created_at_tz || record.created_at,
    "MMM d, yyyy",
  );

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={onClick}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Video size={14} className="text-primary shrink-0" />
            {provider && (
              <Badge variant="outline" className="text-[10px]">{provider}</Badge>
            )}
            {language && (
              <Badge variant="secondary" className="text-[10px]">{language}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {recordingUrl && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(recordingUrl, "_blank");
                }}
              >
                <ExternalLink size={13} />
              </Button>
            )}
            {date && <span className="text-xs text-muted-foreground">{date}</span>}
          </div>
        </div>

        {summary ? (
          <div className="text-[13px] text-muted-foreground line-clamp-4">
            <MarkdownRenderer>{summary.slice(0, 300)}</MarkdownRenderer>
          </div>
        ) : (
          <p className="text-[13px] text-muted-foreground italic">No summary available.</p>
        )}

        {actionItems.length > 0 && (
          <div className="flex items-center gap-1.5">
            <ListChecks size={13} className="text-emerald-600" />
            <span className="text-xs font-medium text-muted-foreground">
              {actionItems.length} action item{actionItems.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RecordingsView = ({ records, searchQuery = "" }: Props) => {
  const [selected, setSelected] = useState<any>(null);
  const filtered = records.filter((r) => matchesSearch(r, searchQuery));

  if (filtered.length === 0) {
    return (
      <EmptyTabState
        title="No recordings yet"
        description={searchQuery ? "Try adjusting your search query." : "Meeting recordings associated with this account will appear here."}
        icon={<Video size={24} />}
      />
    );
  }

  const actionItems = selected ? parseList(selected.action_items || selected.native_action_items) : [];
  const actionItemsMd = actionItems.length > 0
    ? actionItems.map((item, i) => `${i + 1}. ${item}`).join("\n")
    : "";

  return (
    <>
      <div className="flex flex-col gap-3 p-4">
        {filtered.map((record: any) => (
          <RecordingCard
            key={record.id || record.external_id || record.native_external_id || Math.random()}
            record={record}
            onClick={() => setSelected(record)}
          />
        ))}
      </div>

      {selected && (
        <ObjectDetailSheet
          open={!!selected}
          onClose={() => setSelected(null)}
          title="Meeting Recording"
          subtitle={getField(selected, "provider", "native_provider", "source", "native_source")}
          sourceUrl={getField(selected, "recording_url", "native_recording_url")}
          fields={[
            { label: "Provider", value: getField(selected, "provider", "native_provider"), type: "badge" },
            { label: "Language", value: getField(selected, "language", "native_language") },
            { label: "Created", value: selected.created_at_tz || selected.native_created_at_tz || selected.created_at, type: "date" },
            ...(actionItemsMd
              ? [{ label: "Action Items", value: actionItemsMd, type: "markdown" as const }]
              : []),
          ]}
          bodyMarkdown={getField(selected, "summary_md", "native_summary_md") || getField(selected, "transcript_text", "native_transcript_text")}
          bodyLabel={getField(selected, "summary_md", "native_summary_md") ? "Summary" : "Transcript"}
        />
      )}
    </>
  );
};

export default RecordingsView;
